import React, { useEffect, useMemo, useState } from 'react';
import { storage } from '../../utils/platform';
import { useReviews } from '../hooks/useReviews';
import ReviewsHeader from '../sections/ReviewsHeader';
import ReviewsActionsBar from '../sections/ReviewsActionsBar';
import ReviewFilterPills from '../components/ReviewFilterPills';
import ReviewCard from '../components/ReviewCard';
import EmptyState from '../components/EmptyState';
import FabButton from '../components/FabButton';
import CollectReviewModal from '../sections/CollectReviewModal';
import ReviewDetailModal from '../sections/ReviewDetailModal';
import { API_BASE } from '../../config/api';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import LockedNotice from '../components/LockedNotice';

function priorityCompare(a, b) {
  // À répondre (pending ou null) d'abord, puis published, puis ignored.
  const order = (r) => {
    const s = r?.reply_status;
    if (s == null || s === 'pending') return 0;
    if (s === 'published') return 1;
    return 2;
  };
  const oa = order(a); const ob = order(b);
  if (oa !== ob) return oa - ob;
  // Puis date desc
  const ta = Number(a?.review_time) || 0;
  const tb = Number(b?.review_time) || 0;
  return tb - ta;
}

function applyFilter(reviews, filter, fallbackMode) {
  if (fallbackMode || filter === 'all') return reviews;
  if (filter === 'pending') {
    return reviews.filter(r => r.reply_status == null || r.reply_status === 'pending');
  }
  if (filter === 'published') {
    return reviews.filter(r => r.reply_status === 'published');
  }
  return reviews;
}

export default function MobileAvis() {
  // Droits du plan (chantier « palier Découverte »). Tant que l'API n'a pas
  // répondu, on considère la capacité ACQUISE : mieux vaut un bouton qui
  // renvoie un 403 qu'un cadenas affiché à tort à un abonné Pro.
  const { capabilities } = useCurrentRestaurant();
  const peutCollecter = capabilities ? capabilities.manual_collect === true : true;
  const {
    reviews,
    totalGlobal,
    loading,
    syncing,
    error,
    lastSyncAt,
    fallbackMode,
    sync,
    generateAiReply,
    publishReply,
    ignoreReply,
    sendSmsCollect,
  } = useReviews();

  const [filter, setFilter] = useState('all');
  const [collectOpen, setCollectOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [gmbConnected, setGmbConnected] = useState(false);
  const [restaurantNote, setRestaurantNote] = useState(null);

  // Fetch léger : note Google + état GMB (utilisé dans header + bouton publier)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await storage.get('token');
        if (!token) return;
        const headers = { 'Authorization': `Bearer ${token}` };
        const [dashRes, gmbRes] = await Promise.all([
          fetch(`${API_BASE}/api/dashboard`, { headers }).catch(() => null),
          fetch(`${API_BASE}/api/gmb/status`, { headers }).catch(() => null),
        ]);
        if (cancelled) return;
        if (dashRes && dashRes.ok) {
          const d = await dashRes.json();
          setRestaurantNote(d?.restaurant?.note_actuelle ?? d?.restaurant?.note_depart ?? null);
        }
        if (gmbRes && gmbRes.ok) {
          const g = await gmbRes.json();
          setGmbConnected(!!g?.connected);
        }
      } catch (e) {
        console.warn('[MobileAvis] light fetch failed:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const sortedReviews = useMemo(() => [...reviews].sort(priorityCompare), [reviews]);
  const visibleReviews = useMemo(
    () => applyFilter(sortedReviews, filter, fallbackMode),
    [sortedReviews, filter, fallbackMode]
  );

  const counts = useMemo(() => {
    const all = totalGlobal || sortedReviews.length;
    const pending = sortedReviews.filter(r => r.reply_status == null || r.reply_status === 'pending').length;
    const published = sortedReviews.filter(r => r.reply_status === 'published').length;
    return { all, pending, published };
  }, [sortedReviews, totalGlobal]);

  return (
    <div style={styles.page}>
      <ReviewsHeader
        totalGlobal={counts.all}
        note={restaurantNote}
        recentCount={counts.pending}
        fallbackMode={fallbackMode}
      />

      <ReviewFilterPills
        activeFilter={filter}
        onChange={setFilter}
        counts={counts}
        disabled={fallbackMode}
      />

      <ReviewsActionsBar
        onSync={sync}
        syncing={syncing}
        lastSyncAt={lastSyncAt}
      />

      <div style={styles.list}>
        {loading && reviews.length === 0 ? (
          <ListSkeleton />
        ) : error && visibleReviews.length === 0 ? (
          <EmptyState
            emoji="⚠️"
            title="Impossible de charger les avis"
            subtitle={String(error)}
          />
        ) : visibleReviews.length === 0 ? (
          <EmptyState
            emoji="✨"
            title={
              filter === 'pending' ? 'Aucun avis en attente' :
              filter === 'published' ? 'Aucune réponse publiée' :
              'Aucun avis pour le moment'
            }
            subtitle={
              filter === 'all'
                ? 'Synchronisez avec Google ou collectez un nouvel avis.'
                : 'Changez de filtre pour voir d\'autres avis.'
            }
          />
        ) : (
          visibleReviews.map((r, idx) => (
            <ReviewCard
              key={`${r.review_id || idx}-${r.author_name || 'anon'}-${r.review_time || idx}`}
              review={r}
              fallbackMode={fallbackMode}
              onTap={() => setSelected(r)}
            />
          ))
        )}
      </div>

      {/* Pas de bouton d'appel à l'action pour une fonctionnalité non incluse :
          l'utilisateur remplirait un formulaire pour se heurter à un 403. */}
      {peutCollecter && (
        <FabButton onClick={() => setCollectOpen(true)} ariaLabel="Collecter un avis" />
      )}

      <CollectReviewModal
        open={collectOpen}
        onClose={() => setCollectOpen(false)}
        onSend={sendSmsCollect}
      />

      <ReviewDetailModal
        review={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        gmbConnected={gmbConnected}
        onGenerateAi={generateAiReply}
        onPublish={publishReply}
        onIgnore={ignoreReply}
        fallbackMode={fallbackMode}
      />

      <style>{`
        @keyframes revyoPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div>
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={styles.skeletonRow}>
          <div style={styles.skeletonAvatar} />
          <div style={styles.skeletonBody}>
            <div style={{ ...styles.skeletonLine, width: '40%' }} />
            <div style={{ ...styles.skeletonLine, width: '70%', marginTop: 8 }} />
            <div style={{ ...styles.skeletonLine, width: '90%', marginTop: 6 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100dvh',
    background: '#EDEAE3',
    paddingBottom: 32,
  },
  list: {
    background: '#FFFFFF',
    margin: '0 16px',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  skeletonRow: {
    display: 'flex',
    gap: 12,
    padding: '14px 16px',
    borderBottom: '1px solid #EDEAE3',
  },
  skeletonAvatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: '#EDEAE3',
    animation: 'revyoPulse 1.4s ease-in-out infinite',
    flexShrink: 0,
  },
  skeletonBody: {
    flex: 1,
  },
  skeletonLine: {
    height: 10,
    background: '#EDEAE3',
    borderRadius: 6,
    animation: 'revyoPulse 1.4s ease-in-out infinite',
  },
};
